/**
 * Modal "Invitar cliente al portal". Permite invitar a una o más personas a un
 * proyecto en un solo paso. Cada invitación se procesa en el servidor (Edge
 * Function): aquí nunca se ven tokens ni enlaces de activación.
 */
import { useState, useEffect, useMemo } from "react";
import { X, Plus, Trash2, Send, Loader, AlertCircle, Check, UserPlus } from "lucide-react";
import {
  invitarCliente, listarContactosActivos, emailValido, ESTADOS_ACCESO,
} from "../../lib/accesos";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  ok:"#10b981", okL:"#d1fae5", err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
};
const iStyle = {
  width:"100%", padding:"8px 11px", borderRadius:7, border:`1px solid ${C.border}`,
  background:C.white, color:C.text, fontSize:14, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box",
};
const label = { display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5 };

const filaVacia = () => ({ nombre:"", email:"", estado:null, mensaje:"" });

export default function InvitarClienteModal({
  proyectos = [], proyectoInicial, accesosActuales = [], inicial = null, onCerrar, onInvitado,
}) {
  const [proyectoId, setProyectoId] = useState(proyectoInicial || proyectos[0]?.id || "");
  const [contactos,  setContactos]  = useState([]);
  const [contactoId, setContactoId] = useState("");
  const [filas,      setFilas]      = useState([inicial ? { ...filaVacia(), ...inicial } : filaVacia()]);
  const [enviando,   setEnviando]   = useState(false);
  const [errorGeneral, setErrorGeneral] = useState("");

  useEffect(() => {
    listarContactosActivos().then(setContactos).catch(() => setContactos([]));
  }, []);

  // Estado actual de cada correo en el proyecto seleccionado (si ya fue invitado)
  const estadoPorEmail = useMemo(() => {
    const m = {};
    accesosActuales.filter(a => a.proyecto_id === proyectoId)
      .forEach(a => { if (!m[a.email] || a.estado !== "revocada") m[a.email] = a.estado; });
    return m;
  }, [accesosActuales, proyectoId]);

  const setFila = (i, campos) => setFilas(fs => fs.map((f, j) => j === i ? { ...f, ...campos } : f));

  function elegirContacto(id) {
    setContactoId(id);
    const c = contactos.find(x => x.id === id);
    if (c && filas.length === 1 && !filas[0].email && !filas[0].nombre) {
      setFila(0, { nombre: c.razon_social || "", email: c.email || "" });
    }
  }

  const errores = filas.map(f => {
    if (!f.nombre.trim()) return "Falta el nombre";
    if (!emailValido(f.email)) return "Correo inválido";
    return "";
  });
  const correos = filas.map(f => f.email.trim().toLowerCase());
  const duplicados = correos.map((e, i) => e && correos.indexOf(e) !== i);
  const pendientes = filas.filter(f => f.estado !== "ok");
  const puedeEnviar = !!proyectoId && !enviando && pendientes.length > 0
    && filas.every((f, i) => f.estado === "ok" || (!errores[i] && !duplicados[i]));

  async function enviar(e) {
    e?.preventDefault();
    if (!puedeEnviar) return;
    setEnviando(true); setErrorGeneral("");
    let algunaOk = false;
    for (let i = 0; i < filas.length; i++) {
      if (filas[i].estado === "ok") continue;
      setFila(i, { estado: "enviando", mensaje: "" });
      try {
        const r = await invitarCliente({
          proyectoId, email: filas[i].email, nombre: filas[i].nombre, contactoId: contactoId || null,
        });
        setFila(i, { estado: "ok", mensaje: r?.mensaje || "Invitación enviada." });
        algunaOk = true;
      } catch (err) {
        if (err.global) {
          // Falla del servicio (no publicado, sin conexión, sin permisos…): las
          // demás filas fallarían igual, así que no se intentan.
          setFila(i, { estado: "error", mensaje: "No enviada." });
          setErrorGeneral(err.message);
          break;
        }
        setFila(i, { estado: "error", mensaje: err.message });
      }
    }
    setEnviando(false);
    if (algunaOk) onInvitado?.();
  }

  const proyectoNombre = proyectos.find(p => p.id === proyectoId)?.nombre || "";

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="invitar-titulo"
      style={{position:"fixed", inset:0, zIndex:9997, background:"rgba(15,23,42,0.45)",
        backdropFilter:"blur(3px)", display:"flex", alignItems:"flex-start", justifyContent:"center",
        padding:"24px 16px", overflowY:"auto"}}>
      <form onSubmit={enviar}
        style={{background:C.white, borderRadius:14, width:"100%", maxWidth:620,
          boxShadow:"0 24px 80px #00000020", border:`1px solid ${C.border}`, marginBottom:24}}>
        <div style={{padding:"16px 22px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <div style={{display:"flex", alignItems:"center", gap:10}}>
            <div style={{width:32, height:32, borderRadius:8, background:C.accentL,
              display:"flex", alignItems:"center", justifyContent:"center"}}>
              <UserPlus size={15} color={C.accent} aria-hidden="true"/>
            </div>
            <div>
              <h2 id="invitar-titulo" style={{margin:0, fontSize:15, fontWeight:700, color:C.text}}>
                Invitar cliente al portal
              </h2>
              <div style={{fontSize:11, color:C.sub}}>
                Recibirá un correo para activar su acceso de forma segura
              </div>
            </div>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar"
            style={{background:"transparent", border:"none", cursor:"pointer", color:C.light, padding:4}}>
            <X size={17}/>
          </button>
        </div>

        <div style={{padding:"18px 22px", display:"flex", flexDirection:"column", gap:14}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:12}}>
            <div>
              <label htmlFor="inv-proyecto" style={label}>Proyecto que podrá visualizar *</label>
              <select id="inv-proyecto" value={proyectoId} onChange={e => setProyectoId(e.target.value)}
                disabled={enviando} style={iStyle}>
                {proyectos.map(p => <option key={p.id} value={p.id}>{p.nombre} · {p.id}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="inv-contacto" style={label}>
                Empresa o contacto asociado <span style={{fontWeight:400, color:C.light}}>(opcional)</span>
              </label>
              <select id="inv-contacto" value={contactoId} onChange={e => elegirContacto(e.target.value)}
                disabled={enviando} style={iStyle}>
                <option value="">— Sin asociar —</option>
                {contactos.map(c => <option key={c.id} value={c.id}>{c.razon_social}{c.rut ? ` · ${c.rut}` : ""}</option>)}
              </select>
            </div>
          </div>

          <div>
            <div style={label}>Personas a invitar *</div>
            <div style={{display:"flex", flexDirection:"column", gap:8}}>
              {filas.map((f, i) => {
                const existente = estadoPorEmail[f.email.trim().toLowerCase()];
                const est = existente ? ESTADOS_ACCESO[existente] : null;
                const errVisible = (f.nombre || f.email) && f.estado !== "ok" ? (duplicados[i] ? "Correo repetido" : errores[i]) : "";
                return (
                  <div key={i} style={{border:`1px solid ${f.estado === "error" ? C.err + "60" : C.border}`,
                    borderRadius:9, padding:"10px 12px", background: f.estado === "ok" ? C.okL + "60" : C.bg}}>
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1.3fr auto", gap:8, alignItems:"end"}}>
                      <div>
                        <label htmlFor={`inv-nombre-${i}`} style={{...label, fontSize:11}}>Nombre</label>
                        <input id={`inv-nombre-${i}`} value={f.nombre} maxLength={120}
                          disabled={enviando || f.estado === "ok"}
                          onChange={e => setFila(i, { nombre: e.target.value, estado: null, mensaje: "" })}
                          placeholder="Nombre y apellido" style={iStyle}/>
                      </div>
                      <div>
                        <label htmlFor={`inv-email-${i}`} style={{...label, fontSize:11}}>Correo electrónico</label>
                        <input id={`inv-email-${i}`} type="email" value={f.email} maxLength={254}
                          disabled={enviando || f.estado === "ok"}
                          onChange={e => setFila(i, { email: e.target.value, estado: null, mensaje: "" })}
                          placeholder="persona@empresa.cl" style={iStyle}/>
                      </div>
                      <button type="button" aria-label={`Quitar persona ${i + 1}`}
                        disabled={enviando || filas.length === 1}
                        onClick={() => setFilas(fs => fs.filter((_, j) => j !== i))}
                        style={{background:"transparent", border:"none", padding:8, cursor:"pointer",
                          color: filas.length === 1 ? C.border : C.light}}>
                        <Trash2 size={14}/>
                      </button>
                    </div>
                    <div style={{display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:11, flexWrap:"wrap"}}>
                      {est && f.estado !== "ok" && (
                        <span style={{padding:"1px 8px", borderRadius:10, fontWeight:700, color:est.color, background:est.bg}}>
                          Estado actual: {est.label}
                        </span>
                      )}
                      {existente === "pendiente" && f.estado !== "ok" && <span style={{color:C.sub}}>Se reenviará con un enlace nuevo.</span>}
                      {existente === "aceptada" && f.estado !== "ok" && <span style={{color:C.sub}}>Ya tiene acceso; no se enviará otro correo.</span>}
                      {errVisible && <span style={{color:C.err}}>{errVisible}</span>}
                      {f.estado === "enviando" && <span style={{color:C.sub, display:"flex", gap:4, alignItems:"center"}}><Loader size={11} className="spinning"/> Enviando…</span>}
                      {f.estado === "ok" && <span style={{color:C.ok, display:"flex", gap:4, alignItems:"center"}}><Check size={12}/> {f.mensaje}</span>}
                      {f.estado === "error" && <span role="alert" style={{color:C.err, display:"flex", gap:4, alignItems:"center"}}><AlertCircle size={12}/> {f.mensaje}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
            <button type="button" disabled={enviando || filas.length >= 10}
              onClick={() => setFilas(fs => [...fs, filaVacia()])}
              style={{marginTop:8, display:"flex", alignItems:"center", gap:5, padding:"6px 10px",
                borderRadius:7, border:`1px dashed ${C.border}`, background:"transparent", color:C.sub,
                fontSize:12, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
              <Plus size={12}/> Agregar otra persona
            </button>
          </div>

          <p style={{margin:0, fontSize:12, color:C.sub, lineHeight:1.6, background:C.accentL,
            border:`1px solid ${C.accent}30`, borderRadius:8, padding:"9px 12px"}}>
            Cada persona recibirá un correo para <strong>{proyectoNombre || "el proyecto"}</strong>.
            Si su correo ya tiene una cuenta (por ejemplo, de otra aplicación), entrará con ella; si no,
            creará su contraseña. El enlace vence en 7 días y sirve una sola vez.
          </p>

          {errorGeneral && (
            <div role="alert" style={{color:C.err, background:C.errL, border:`1px solid ${C.err}40`, borderRadius:8,
              padding:"9px 12px", fontSize:12, lineHeight:1.5, display:"flex", gap:7, alignItems:"flex-start"}}>
              <AlertCircle size={14} style={{flexShrink:0, marginTop:1}}/> {errorGeneral}
            </div>
          )}

          <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
            <button type="button" onClick={onCerrar}
              style={{padding:"8px 18px", borderRadius:8, border:`1px solid ${C.border}`,
                background:"transparent", color:C.sub, fontSize:14, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
              {filas.some(f => f.estado === "ok") ? "Cerrar" : "Cancelar"}
            </button>
            <button type="submit" disabled={!puedeEnviar}
              style={{display:"flex", alignItems:"center", gap:6, padding:"8px 20px", borderRadius:8,
                border:"none", fontWeight:700, fontSize:14, fontFamily:"'DM Sans',sans-serif",
                background: puedeEnviar ? C.accent : C.light, color:"#fff",
                cursor: puedeEnviar ? "pointer" : "not-allowed"}}>
              {enviando ? <><Loader size={13} className="spinning"/> Enviando…</>
                : <><Send size={13}/> {pendientes.length > 1 ? `Enviar ${pendientes.length} invitaciones` : "Enviar invitación"}</>}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
